import React, { useState } from "react";
import {
    Autocomplete,
    TextField,
    Box,
    CircularProgress,
    Chip,
    createFilterOptions,
} from "@mui/material";
import PropTypes from "prop-types";

const filter = createFilterOptions({
    stringify: (option) => {
        if (typeof option === "string") return option;
        return String(option?.label || "");
    },
});

const CustomSelectField = ({
    value,
    options = [],
    onChange,
    onCreate,
    error,
    disabled,
    isLoading,
    name,
    placeholder = "Select or type to create...",
    multiple = false,
    minLength = 3,
    maxLength = 50,
}) => {
    const [inputValue, setInputValue] = useState("");
    const [isCreating, setIsCreating] = useState(false);
    const [localError, setLocalError] = useState("");

    const handleAction = async (val) => {
        const trimmed = String(val || "").trim();
        if (trimmed.length < minLength) {
            setLocalError(`Minimum ${minLength} characters required`);
            return;
        }
        if (trimmed.length > maxLength) {
            setLocalError(`Maximum ${maxLength} characters allowed`);
            return;
        }

        setLocalError("");
        setIsCreating(true);
        try {
            await onCreate(trimmed);
            setInputValue("");
        } catch (err) {
            console.error("Failed to create new option:", err);
        } finally {
            setIsCreating(false);
        }
    };

    const handleKeyDown = (event) => {
        if (event.key === "Escape") {
            setInputValue("");
        } else if (event.key === "Enter" && !isCreating) {
            const currentSearch = String(inputValue || "").trim();
            if (!currentSearch) return;

            const isExisting = options.some(
                (opt) => String(opt?.label || "").toLowerCase() === currentSearch.toLowerCase()
            );

            if (!isExisting) {
                event.defaultMuiPrevented = true;
                handleAction(currentSearch);
            }
        }
    };

    return (
        <Box sx={{ width: "100%" }}>
            <Autocomplete
                multiple={multiple}
                freeSolo
                disabled={disabled || isLoading || isCreating}
                options={options}
                filterOptions={(options, params) => {
                    const filtered = filter(options, params);
                    const { inputValue: searchVal } = params;

                    const isExisting = options.some(
                        (opt) => String(opt?.label || "").toLowerCase() === String(searchVal || "").toLowerCase()
                    );

                    if (searchVal !== "" && !isExisting) {
                        filtered.unshift({
                            inputValue: searchVal,
                            label: `Create "${searchVal}"`,
                            isNew: true,
                        });
                    }

                    return filtered;
                }}
                getOptionLabel={(option) => {
                    if (typeof option === "string") return option;
                    if (option.isNew) return option.inputValue;
                    return option.label || "";
                }}
                isOptionEqualToValue={(option, val) => {
                    if (!option || !val) return false;
                    return (option.value || option.label) === (val.value || val.label);
                }}
                value={value}
                inputValue={inputValue}
                onInputChange={(event, newInputValue) => {
                    setInputValue(newInputValue || "");
                    if (localError) setLocalError("");
                }}
                onChange={(event, newValue) => {
                    if (multiple && Array.isArray(newValue)) {
                        const lastItem = newValue[newValue.length - 1];
                        if (lastItem?.isNew) {
                            handleAction(lastItem.inputValue);
                        } else {
                            onChange(newValue, { name });
                        }
                    } else {
                        if (newValue?.isNew) {
                            handleAction(newValue.inputValue);
                        } else {
                            onChange(newValue, { name });
                        }
                    }
                }}
                noOptionsText={
                    inputValue
                        ? `Press Enter to create "${inputValue}"`
                        : "No options available"
                }
                renderOption={(props, option) => {
                    const { key, ...otherProps } = props;
                    return (
                        <li {...otherProps} key={key} style={{ padding: '8px 16px', fontSize: '14px' }}>
                            {option.isNew ? (
                                <Box sx={{ display: 'flex', alignItems: 'center', color: '#313ade', fontWeight: 'bold' }}>
                                    {option.label}
                                </Box>
                            ) : (
                                option.label
                            )}
                        </li>
                    );
                }}
                renderTags={(tagValue, getTagProps) =>
                    tagValue.map((option, index) => (
                        <Chip
                            key={index}
                            label={String(option?.label || "")}
                            {...getTagProps({ index })}
                            size="small"
                            sx={{ borderRadius: "4px", backgroundColor: "#5b7db8ff", color: "#fff" }}
                        />
                    ))
                }
                renderInput={(params) => (
                    <TextField
                        {...params}
                        error={error || !!localError}
                        helperText={localError}
                        placeholder={placeholder}
                        onKeyDown={handleKeyDown}
                        size="small"
                        aria-label={name}
                        aria-describedby={`${name}-helper-text`}
                        InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                                <React.Fragment>
                                    {isCreating || isLoading ? (
                                        <CircularProgress color="inherit" size={20} />
                                    ) : null}
                                    {params.InputProps.endAdornment}
                                </React.Fragment>
                            ),
                            sx: {
                                height: multiple ? "auto" : "45px",
                                minHeight: "45px",
                                borderRadius: "8px",
                                fontSize: "0.875rem",
                                "& .MuiOutlinedInput-notchedOutline": {
                                    borderColor: "#d2d6da",
                                },
                                "&:hover .MuiOutlinedInput-notchedOutline": {
                                    borderColor: "#a7caedff",
                                },
                                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                    borderColor: "#2f5dacff",
                                },
                                py: multiple ? 1 : 0
                            },
                        }}
                    />
                )}
                componentsProps={{
                    paper: {
                        sx: {
                            borderRadius: "12px",
                            mt: 0.5,
                            overflow: "hidden",
                            "& .MuiAutocomplete-listbox": {
                                padding: "6px 0",
                                "& .MuiAutocomplete-option": {
                                    "&[aria-selected='true']": {
                                        backgroundColor: "rgba(9, 96, 247, 0.08)",
                                    },
                                    "&.Mui-focused": {
                                        backgroundColor: "rgba(104, 137, 243, 0.08)",
                                    },
                                    "&:hover": {
                                        backgroundColor: "rgba(80, 197, 251,0.4)",
                                    }
                                }
                            }
                        }
                    }
                }}
            />
        </Box>
    );
};

CustomSelectField.propTypes = {
    value: PropTypes.oneOfType([PropTypes.object, PropTypes.string, PropTypes.array]),
    options: PropTypes.arrayOf(
        PropTypes.shape({
            value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            label: PropTypes.string,
        })
    ).isRequired,
    onChange: PropTypes.func.isRequired,
    onCreate: PropTypes.func.isRequired,
    error: PropTypes.bool,
    disabled: PropTypes.bool,
    isLoading: PropTypes.bool,
    name: PropTypes.string,
    placeholder: PropTypes.string,
    multiple: PropTypes.bool,
    minLength: PropTypes.number,
    maxLength: PropTypes.number,
};

export default CustomSelectField;
